import { useMemo } from "react";
import { toast } from "sonner";
import { exportarXLSX } from "../../../utilitarios/excel";
import {
  CONSULTA_TIPOS_ROTULOS,
  consultaTiposSelecionadosList,
  formatarConsultaTiposResumo,
  normalizarConsultaTipos,
} from "./consultaTiposUtils";

const formatarDataHora = (valor) => {
  if (!valor) {
    return "-";
  }
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) {
    return valor;
  }
  return data.toLocaleString("pt-BR");
};

const formatarDataCurta = (valor) => {
  if (!valor) {
    return "";
  }
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) {
    return valor;
  }
  return data.toLocaleDateString("pt-BR");
};

const formatarValorExibicao = (valor) => {
  if (typeof valor === "number" && Number.isFinite(valor)) {
    return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }
  return valor;
};

const extrairMultas = (veiculo) => {
  const debitos = Array.isArray(veiculo?.debitos) ? veiculo.debitos : [];
  return debitos.flatMap((debito) => {
    const multas = Array.isArray(debito?.multas) ? debito.multas : [];
    return multas.map((multa) => ({
      ...multa,
      tipoDebito: debito?.tipoDebito || debito?.tipo || debito?.descricao || debito?.descrInfracao,
    }));
  });
};

const formatarResumoItem = (veiculo) => {
  if (veiculo?.erro || veiculo?.error) {
    return { texto: veiculo?.erro || veiculo?.error, variante: "danger" };
  }
  const debitos = Array.isArray(veiculo?.debitos) ? veiculo.debitos.length : 0;
  const multas = extrairMultas(veiculo).length;
  if (debitos === 0 && multas === 0) {
    return { texto: "Sem débitos retornados", variante: "success" };
  }
  return {
    texto: `${debitos} débito(s), ${multas} multa(s)`,
    variante: multas > 0 ? "warning" : "info",
  };
};

const ensureStatusString = (valor) => (valor === undefined || valor === null ? "" : String(valor));

const normalizarStatusTexto = (valor) => ensureStatusString(valor).toLowerCase();

const mapearStatusItemVisual = (status) => {
  const valor = normalizarStatusTexto(status);
  if (["sucesso", "success", "done", "completed", "ok"].includes(valor)) {
    return { variante: "success", rotulo: "Concluído" };
  }
  if (["erro", "error", "failed", "falha"].includes(valor)) {
    return { variante: "danger", rotulo: "Erro" };
  }
  if (["warning", "aviso", "alerta"].includes(valor)) {
    return { variante: "warning", rotulo: "Aviso" };
  }
  if (["idle"].includes(valor)) {
    return { variante: "neutral", rotulo: "Aguardando" };
  }
  return { variante: "pending", rotulo: "Processando" };
};

const CARD_VARIANT_STYLES = {
  success: { backgroundColor: "#e6f7ef", borderColor: "#b6e3c8" },
  danger: { backgroundColor: "#fdeaea", borderColor: "#f5c2bf" },
  warning: { backgroundColor: "#fff4e6", borderColor: "#ffd8a8" },
  info: { backgroundColor: "#edf4ff", borderColor: "#c7d7fe" },
  pending: { backgroundColor: "#f1f5f9", borderColor: "#cbd5f5" },
  neutral: { backgroundColor: "#f1f5f9", borderColor: "#cbd5f5" },
  default: { backgroundColor: "#f1f5f9", borderColor: "#cbd5f5" },
};

const normalizarRenavamChave = (valor) => {
  const texto = (valor ?? "").toString().trim();
  if (!texto) {
    return "";
  }
  const numeros = texto.replace(/\D+/g, "");
  if (numeros) {
    const semZeros = numeros.replace(/^0+/, "");
    return semZeros || "0";
  }
  return texto;
};

const gerarChaveItem = (item) => {
  if (!item) {
    return null;
  }
  const renavam = normalizarRenavamChave(item.renavam || item.veiculo?.renavam || "");
  if (renavam) {
    return `renavam:${renavam}`;
  }
  const jobId =
    item.jobItemId ||
    item.jobItemKey ||
    item.raw?.jobItemId ||
    item.raw?.jobItemKey ||
    item.raw?.id;
  if (jobId) {
    return `job:${jobId}`;
  }
  const ordemValida = Number.isFinite(item.ordem) ? item.ordem : null;
  if (ordemValida !== null) {
    return `ordem:${ordemValida}`;
  }
  return null;
};

const STATUS_PAINEL_ROTULOS = {
  loading: "Consultando DETRAN...",
  sucesso: "Consulta concluída",
  erro: "Erro ao consultar",
  idle: "Aguardando consulta",
};

const STATUS_PAINEL_CLASSES = {
  loading: "info",
  sucesso: "success",
  erro: "danger",
  idle: "neutral",
};

const ensureString = (valor) => (valor === undefined || valor === null ? "" : String(valor));

const normalizarNomeArquivo = (valor) => {
  const texto = ensureString(valor).trim();
  if (!texto) {
    return "";
  }
  return texto
    .replace(/[^\w-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

const montarNomeArquivoGuia = (renavam, guiaPdf) => {
  const partes = ["guia"];
  if (guiaPdf?.tipo) {
    partes.push(normalizarNomeArquivo(guiaPdf.tipo));
  }
  if (renavam) {
    partes.push(normalizarNomeArquivo(renavam));
  }
  if (guiaPdf?.referencia) {
    partes.push(normalizarNomeArquivo(guiaPdf.referencia));
  }
  const nomeBase = partes.filter(Boolean).join("-");
  return `${nomeBase || "guia"}.pdf`;
};

const extrairCodigoBarras = (entrada = {}) => {
  const candidatos = [
    entrada.codigoBarras,
    entrada.codigoBarra,
    entrada.codigoBarrasGRD,
    entrada.codigoPagamento,
    entrada.barcode,
    entrada.cdbarras,
    entrada.cdBarras,
  ];
  return candidatos.map((item) => ensureString(item).trim()).find(Boolean) || "";
};

const extrairCodigoPix = (entrada = {}) => {
  const candidatos = [
    entrada.pixCopiaCola,
    entrada.copiaCola,
    entrada.codigoPix,
    entrada.codigoPagamentoPix,
    entrada.txId,
    entrada.txid,
    entrada.qrCode,
    entrada.qrcode,
    entrada.codigoQr,
  ];
  return candidatos.map((item) => (item || "").toString().trim()).find(Boolean) || "";
};

const extrairValorLinhaDigitavel = (linhaDigitavel) => {
  const numeros = ensureString(linhaDigitavel).replace(/\D+/g, "");
  if (!numeros) {
    return null;
  }
  if (numeros.length === 47) {
    const valorTexto = numeros.slice(37, 47);
    if (!valorTexto) {
      return null;
    }
    const valor = Number.parseInt(valorTexto, 10) / 100;
    return Number.isFinite(valor) ? valor : null;
  }
  if (numeros.length === 48) {
    const semDv =
      numeros.slice(0, 11) +
      numeros.slice(12, 23) +
      numeros.slice(24, 35) +
      numeros.slice(36, 47);
    if (semDv.length !== 44) {
      return null;
    }
    const tipoValor = semDv[2];
    if (!["6", "8"].includes(tipoValor)) {
      return null;
    }
    const valorTexto = semDv.slice(4, 15);
    const valor = Number.parseInt(valorTexto, 10) / 100;
    return Number.isFinite(valor) ? valor : null;
  }
  return null;
};

const extrairValorPix = (codigoPix) => {
  const codigo = ensureString(codigoPix).replace(/\s+/g, "");
  if (!codigo) {
    return null;
  }
  let idx = 0;
  while (idx + 4 <= codigo.length) {
    const tag = codigo.slice(idx, idx + 2);
    const lenTexto = codigo.slice(idx + 2, idx + 4);
    const tamanho = Number.parseInt(lenTexto, 10);
    if (!Number.isFinite(tamanho) || tamanho <= 0) {
      return null;
    }
    const inicioValor = idx + 4;
    const fimValor = inicioValor + tamanho;
    if (fimValor > codigo.length) {
      return null;
    }
    const valor = codigo.slice(inicioValor, fimValor);
    if (tag === "54") {
      const numero = Number.parseFloat(valor.replace(",", "."));
      return Number.isFinite(numero) ? numero : null;
    }
    idx = fimValor;
  }
  return null;
};

const extrairValorPagamento = (entrada = {}) => {
  const candidatos = [
    entrada.valorGRD,
    entrada.valor,
    entrada.valorOriginal,
    entrada.valorAtual,
    entrada.valorACobrar,
  ];
  const encontrado = candidatos.find(
    (valor) => typeof valor === "number" || (typeof valor === "string" && valor.trim() !== ""),
  );
  if (encontrado !== undefined && encontrado !== null && encontrado !== "") {
    return encontrado;
  }
  const valorLinha = extrairValorLinhaDigitavel(
    entrada.linhaDigitavel || entrada.linhadigitavel || entrada.digitableLine,
  );
  if (valorLinha !== null) {
    return valorLinha;
  }
  const valorPix = extrairValorPix(extrairCodigoPix(entrada));
  if (valorPix !== null) {
    return valorPix;
  }
  return "";
};

const extrairVencimentoPagamento = (entrada = {}) =>
  entrada.dataVencimentoGRD ||
  entrada.dataVencimento ||
  entrada.dataLimitePagto ||
  entrada.vencimento ||
  entrada.dtVencimento ||
  entrada.dtvencimento ||
  "";

const extrairPdfBase64 = (entrada = {}) => {
  const candidatos = [entrada.pdfBase64, entrada.guiaBase64];
  return candidatos.map((item) => ensureString(item).trim()).find(Boolean) || "";
};

const extrairTipoDescricao = (tipo, guia, fallback = "") => {
  if (tipo === "Multa") {
    return (
      fallback ||
      guia?.numAutoInfracao ||
      guia?.codAuto ||
      guia?.referencia ||
      guia?.descricao ||
      "Multa"
    );
  }
  if (tipo === "Licenciamento") {
    return guia?.referencia || "Licenciamento";
  }
  return guia?.referencia || fallback || tipo;
};

const montarLinhasExportacao = (itens = []) => {
  const linhas = [];

  itens.forEach((item) => {
    const renavam = normalizarRenavamChave(item?.veiculo?.renavam || item?.renavam || "");
    const placa = ensureString(
      item?.veiculo?.placa ||
        item?.veiculo?.placaVeiculo ||
        item?.responseBody?.placa ||
        item?.placa,
    )
      .toUpperCase()
      .trim();

    const adicionarLinha = (tipo, guia, descricaoFallback = "") => {
      if (!guia || typeof guia !== "object") {
        return;
      }
      const codigoBarras = extrairCodigoBarras(guia);
      const codigoPix = extrairCodigoPix(guia);
      const valor = extrairValorPagamento(guia);
      const vencimento = extrairVencimentoPagamento(guia);
      const pdfBase64 = extrairPdfBase64(guia);
      if (!codigoBarras && !codigoPix && !valor && !vencimento && !pdfBase64) {
        return;
      }
      const descricaoFinal = extrairTipoDescricao(
        tipo,
        guia,
        descricaoFallback || (pdfBase64 ? "Guia PDF" : ""),
      );
      linhas.push([
        renavam || "-",
        placa || "-",
        tipo,
        descricaoFinal || "-",
        valor,
        vencimento ? formatarDataCurta(vencimento) : "",
        codigoBarras || "",
        codigoPix || "",
      ]);
    };

    const ipvaGuias = normalizarGuiaLista(item?.responseBody?.ipva?.payload);
    ipvaGuias.forEach((guia) => adicionarLinha("IPVA", guia));

    const multasGuias = normalizarGuiaLista(item?.responseBody?.multas?.guias);
    multasGuias.forEach((entrada) => {
      const guia = entrada?.guia || entrada;
      adicionarLinha(
        "Multa",
        guia,
        entrada?.codAuto || entrada?.numAutoInfracao || entrada?.descricao || "",
      );
    });

    const licenciamentoGuias = normalizarGuiaLista(item?.responseBody?.licenciamento?.guiaAtual).filter(
      guiaPossuiDadosPagamento,
    );
    licenciamentoGuias.forEach((guia) => adicionarLinha("Licenciamento", guia));
  });

  return linhas;
};

const normalizarGuiaLista = (entrada) => {
  if (!entrada) {
    return [];
  }
  if (Array.isArray(entrada)) {
    return entrada;
  }
  if (typeof entrada === "object") {
    return [entrada];
  }
  return [];
};

const normalizarMultasConsulta = (entrada) => {
  if (!entrada) {
    return [];
  }
  if (Array.isArray(entrada)) {
    return entrada;
  }
  if (Array.isArray(entrada?.payload)) {
    return entrada.payload;
  }
  return [];
};

const guiaPossuiDadosPagamento = (guia) => {
  if (!guia || typeof guia !== "object") {
    return false;
  }
  const camposRelevantes = [
    "linhaDigitavel",
    "linhadigitavel",
    "digitableLine",
    "codigoBarras",
    "codigoBarra",
    "codigoBarrasGRD",
    "cdbarras",
    "cdBarras",
    "codigoPix",
    "pixCopiaCola",
    "qrcode",
    "qrCode",
    "valor",
    "valorOriginal",
    "valorGRD",
    "dtvencimento",
    "dtVencimento",
    "guiaBase64",
    "pdfBase64",
  ];
  return camposRelevantes.some((campo) => guia[campo] !== undefined && guia[campo] !== null && guia[campo] !== "");
};

const extrairMensagensLicenciamento = (info = {}) => {
  const mensagens = [];
  if (info?.guiaMensagem) {
    mensagens.push(info.guiaMensagem);
  }
  const mensagensDetran = Array.isArray(info?.guiaAtual?.mensagens)
    ? info.guiaAtual.mensagens
    : [];
  mensagensDetran.forEach((entrada) => {
    const texto = entrada?.mensagem || entrada?.message;
    if (texto) {
      mensagens.push(`DETRANPR: ${texto}`);
    }
  });
  return mensagens;
};

const obterValorCampo = (obj, campos) => {
  if (!obj) {
    return "";
  }
  for (const campo of campos) {
    const valor = obj?.[campo];
    if (valor !== undefined && valor !== null && valor !== "") {
      return valor;
    }
  }
  return "";
};

const copiarValor = async (texto, descricao = "valor") => {
  if (!texto) {
    return;
  }
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(`${descricao} copiado para a área de transferência.`);
  } catch (_erro) {
    toast.error(`Não foi possível copiar o ${descricao}.`);
  }
};

const truncarCodigo = (codigo, limite = 24) => {
  if (!codigo) {
    return "";
  }
  const texto = codigo.toString();
  if (texto.length <= limite) {
    return texto;
  }
  return `${texto.slice(0, limite)}…`;
};

const renderCodigoCopiavel = (rotulo, codigo, descricao) => {
  if (!codigo) {
    return null;
  }
  const textoTruncado = truncarCodigo(codigo);
  return (
    <div className="tw-mt-2 tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-100 tw-px-3 tw-py-2 tw-text-[11px] tw-font-mono dark:tw-border-slate-600 dark:tw-bg-slate-900/50">
      <div className="tw-flex tw-flex-col tw-gap-0.5 tw-text-left tw-text-slate-800 dark:tw-text-slate-200">
        <span className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-slate-300">
          {rotulo}
        </span>
        <span title={codigo}>{textoTruncado}</span>
      </div>
      <button
        type="button"
        className="btn-icon"
        aria-label={`Copiar ${descricao}`}
        onClick={() => copiarValor(codigo, descricao)}
      >
        <span className="material-symbols-outlined tw-text-base">content_copy</span>
      </button>
    </div>
  );
};

const renderGuiaPagamento = (guia, idx) => {
  if (!guia || typeof guia !== "object") {
    return null;
  }
  const referencia = obterValorCampo(guia, [
    "referencia",
    "nrreferencia",
    "numero",
    "descricao",
    "tipo",
    "anoExercicio",
    "exercicio",
  ]);
  const vencimento = extrairVencimentoPagamento(guia);
  const valorPagamento = extrairValorPagamento(guia);
  const valorAtual = obterValorCampo(guia, ["valorAtual", "valorAtualizado", "valorCorrigido"]);
  const valorPagamentoExibido = formatarValorExibicao(valorPagamento);
  const valorAtualExibido = formatarValorExibicao(valorAtual);
  const linhaDigitavel = obterValorCampo(guia, ["linhaDigitavel", "linhadigitavel", "digitableLine"]);
  const codigoBarras = extrairCodigoBarras(guia) || null;
  const codigoPix = extrairCodigoPix(guia);
  const pdfBase64 = extrairPdfBase64(guia);
  const temDetalhes = Boolean(
    valorPagamento || valorAtual || vencimento || linhaDigitavel || codigoBarras || codigoPix || pdfBase64,
  );

  return (
    <div key={idx} className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-2 dark:tw-border-slate-700 dark:tw-bg-slate-900/40">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <strong>{referencia || "Guia"}</strong>
        {vencimento && (
          <span className="tw-text-[11px] tw-font-semibold tw-text-slate-500 dark:tw-text-slate-300">
            Vence em {formatarDataCurta(vencimento)}
          </span>
        )}
      </div>
      <div className="tw-mt-1 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
        {valorPagamento && <div>Valor: {valorPagamentoExibido}</div>}
        {valorAtual && <div>Atualizado: {valorAtualExibido}</div>}
        {renderCodigoCopiavel("Linha digitável", linhaDigitavel, "linha digitável")}
        {renderCodigoCopiavel("Código de barras", codigoBarras, "código de barras")}
        {renderCodigoCopiavel("Código PIX", codigoPix, "código PIX")}
        {pdfBase64 && (
          <div className="tw-mt-1 tw-text-[11px] tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-200">
            PDF disponível para download.
          </div>
        )}
        {!temDetalhes && <div className="tw-text-[11px]">Detalhes da guia não informados.</div>}
      </div>
    </div>
  );
};

const salvarPdfBase64 = (nomeArquivo, conteudoBase64) => {
  if (typeof window === "undefined" || !conteudoBase64) {
    return;
  }
  try {
    const base64Limpo = ensureString(conteudoBase64)
      .replace(/^data:application\/pdf;base64,/, "")
      .replace(/\s+/g, "");
    if (!base64Limpo) {
      return;
    }
    const byteCharacters = atob(base64Limpo);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = nomeArquivo || "guia.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (_erro) {
    toast.error("Não foi possível baixar o PDF desta guia.");
  }
};
function DebitosLotePanel({ aberto, onFechar, estado, carregando, onLimpar, resumo }) {
  const itens = useMemo(
    () => (Array.isArray(estado?.itens) ? estado.itens : []),
    [estado?.itens],
  );

  const handleOverlayClick = () => onFechar?.();
  const stopPropagation = (event) => event.stopPropagation();

  const statusAtual = estado?.status || "idle";
  const statusRotulo = STATUS_PAINEL_ROTULOS[statusAtual] || STATUS_PAINEL_ROTULOS.idle;
  const statusClasse = STATUS_PAINEL_CLASSES[statusAtual] || STATUS_PAINEL_CLASSES.idle;
  const exibindoProgresso = statusAtual === "loading";
  const jobResumo = estado?.jobResumo;
  const consultaTiposOrigem = jobResumo?.consultaTipos || estado?.parametros?.consultaTipos;
  const consultaTiposNormalizados = normalizarConsultaTipos(consultaTiposOrigem);
  const consultaTiposLista = consultaTiposSelecionadosList(consultaTiposNormalizados);
  const consultaTiposResumo = formatarConsultaTiposResumo(consultaTiposNormalizados);
  const percentualProcessado =
    jobResumo && Number.isFinite(jobResumo.total) && jobResumo.total > 0
      ? Math.min(100, Math.round((jobResumo.processed / jobResumo.total) * 100))
      : null;
  const processamentoPendente = statusAtual === "loading";
  const progressoPercentual = percentualProcessado ?? 0;
  const progressoRotulo =
    percentualProcessado !== null
      ? `${percentualProcessado}% dos veículos processados`
      : "Aguardando progresso do processamento";
  const totalSelecionados = estado?.parametros?.renavams?.length || itens.length || 0;
  const itensOrdenados = useMemo(
    () => [...itens].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)),
    [itens],
  );
  const itensUnicos = useMemo(() => {
    if (itensOrdenados.length === 0) {
      return itensOrdenados;
    }
    const vistos = new Set();
    const acumulado = [];
    for (let i = itensOrdenados.length - 1; i >= 0; i -= 1) {
      const item = itensOrdenados[i];
      const chave = gerarChaveItem(item);
      if (!chave) {
        acumulado.push(item);
        continue;
      }
      if (vistos.has(chave)) {
        continue;
      }
      vistos.add(chave);
      acumulado.push(item);
    }
    return acumulado.reverse();
  }, [itensOrdenados]);
  const linhasExportacao = useMemo(() => montarLinhasExportacao(itensUnicos), [itensUnicos]);
  const totaisCalculados = useMemo(() => {
    let veiculosComRetorno = 0;
    let debitosTotal = 0;
    let multasTotal = 0;
    itensUnicos.forEach((item) => {
      const veiculoAtual = item?.veiculo;
      if (!veiculoAtual) {
        return;
      }
      veiculosComRetorno += 1;
      const debitosVeiculo = Array.isArray(veiculoAtual.debitos) ? veiculoAtual.debitos : [];
      debitosTotal += debitosVeiculo.length;
      multasTotal += extrairMultas(veiculoAtual).length;
    });
    return { veiculosComRetorno, debitosTotal, multasTotal };
  }, [itensUnicos]);
  const exportacaoDesabilitada =
    processamentoPendente || linhasExportacao.length === 0 || totalSelecionados === 0;
  const tituloExportacao = processamentoPendente
    ? "Aguarde concluir todas as consultas para exportar."
    : "Exporta XLS apenas dos veículos selecionados no lote";
  const totalVeiculosRetornados =
    totaisCalculados.veiculosComRetorno ||
    resumo?.totalVeiculos ||
    itensUnicos.length ||
    0;
  const nenhumItem = itensUnicos.length === 0;
  const tempoProcessamento = useMemo(() => {
    const inicio =
      estado?.iniciadoEm ||
      estado?.parametros?.iniciadoEm ||
      jobResumo?.startedAt ||
      jobResumo?.raw?.startedAt ||
      null;
    const fim =
      estado?.recebidoEm ||
      jobResumo?.finishedAt ||
      jobResumo?.raw?.finishedAt ||
      jobResumo?.updatedAt ||
      null;
    console.log("[DebitosLotePanel] Tempo processamento debug", {
      inicio,
      fim,
      jobResumo,
      parametros: estado?.parametros,
    });
    if (!inicio || !fim) {
      return null;
    }
    const inicioMs = new Date(inicio).getTime();
    const fimMs = new Date(fim).getTime();
    if (Number.isNaN(inicioMs) || Number.isNaN(fimMs) || fimMs < inicioMs) {
      return null;
    }
    const segundos = Math.round((fimMs - inicioMs) / 1000);
    if (segundos < 60) {
      return `${segundos}s`;
    }
    const minutos = Math.floor(segundos / 60);
    const resto = segundos % 60;
    return `${minutos}m ${resto.toString().padStart(2, "0")}s`;
  }, [estado?.iniciadoEm, estado?.parametros, estado?.recebidoEm, jobResumo]);

  if (!aberto) {
    return null;
  }

  const handleExportar = () => {
    if (!itensUnicos.length) {
      toast.error("Realize a consulta em lote para exportar o XLS.");
      return;
    }
    if (linhasExportacao.length === 0) {
      toast.error("Nenhum pagamento retornado para exportar.");
      return;
    }
    const cabecalho = [
      "RENAVAM",
      "Placa",
      "Tipo",
      "Descrição",
      "Valor",
      "Vencimento",
      "Código de Barras",
      "Código PIX/TxID",
    ];
    exportarXLSX({
      arquivo: "debitos-lote",
      aba: "Pagamentos",
      cabecalho,
      linhas: linhasExportacao,
      colunasNumericas: [4],
    });
    toast.success("Planilha de pagamentos exportada.");
  };

  return (
    <div className="lote-progress-overlay" onClick={handleOverlayClick}>
      <aside className="lote-progress-panel" onClick={stopPropagation}>
        <div className="lote-progress-panel__header">
          <div>
            <h3>Débitos em lote</h3>
            <p className="lote-progress-panel__sub">
              {statusRotulo}
              {estado?.recebidoEm && ` · ${formatarDataHora(estado.recebidoEm)}`}
            </p>
            {jobResumo?.jobKey && (
              <p className="tw-m-0 tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">
                Job: {jobResumo.jobKey}
              </p>
            )}
            {tempoProcessamento && statusAtual !== "loading" && (
              <p className="tw-mt-1 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
                Tempo total: {tempoProcessamento}
              </p>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            {onLimpar && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onLimpar}>
                Limpar
              </button>
            )}
            <button type="button" className="btn-icon" onClick={() => onFechar?.()}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>

        <div className="lote-progress-panel__summary">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
            <div className={`status-pill ${statusClasse}`}>{statusRotulo}</div>
            {(carregando || exibindoProgresso) && (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-300">
                <span className="tw-inline-flex tw-h-2 tw-w-2 tw-animate-ping tw-rounded-full tw-bg-amber-500" />
                Consultando...
              </span>
            )}
          </div>
          {jobResumo && (
            <div className="tw-mt-3 tw-w-full" aria-live="polite">
              <div
                className="progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progressoPercentual}
              >
                <div className="progress-bar__fill" style={{ width: `${progressoPercentual}%` }} />
              </div>
              <div className="tw-mt-1 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-300">
                {progressoRotulo}
              </div>
            </div>
          )}
          {consultaTiposLista.length > 0 && (
            <div className="tw-mt-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500 dark:tw-text-slate-400">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span>Blocos consultados:</span>
                <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                  {consultaTiposLista.map((tipo) => (
                    <span
                      key={tipo}
                      className="tw-rounded-full tw-bg-slate-200 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-slate-700 dark:tw-bg-slate-800 dark:tw-text-slate-200"
                    >
                      {CONSULTA_TIPOS_ROTULOS[tipo]}
                    </span>
                  ))}
                </div>
              </div>
              <p className="tw-mt-1 tw-text-[11px] tw-font-semibold tw-uppercase tw-text-slate-500 dark:tw-text-slate-400">
                Consultando: {consultaTiposResumo}
              </p>
            </div>
          )}
          {jobResumo && (
            <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-3 tw-text-xs tw-text-slate-500 dark:tw-text-slate-300">
              <span>
                Processados: <strong>{jobResumo.processed || 0}</strong>/
                {jobResumo.total || totalSelecionados}
              </span>
              <span>
                Sucesso: <strong>{jobResumo.success || 0}</strong>
              </span>
              <span>
                Erros: <strong>{jobResumo.failed || 0}</strong>
              </span>
              {percentualProcessado !== null && (
                <span>
                  Progresso: <strong>{percentualProcessado}%</strong>
                </span>
              )}
            </div>
          )}
          <ul>
            <li>
              Renavams enviados: <strong>{totalSelecionados}</strong>
            </li>
            <li>
              Veículos retornados: <strong>{totalVeiculosRetornados}</strong>
            </li>
          </ul>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <button
              type="button"
              className="btn-export-excel tw-min-w-[200px] tw-justify-center tw-disabled:cursor-not-allowed tw-disabled:opacity-50 dark:tw-shadow-none"
              onClick={handleExportar}
              disabled={exportacaoDesabilitada}
              title={tituloExportacao}
            >
              <span className="material-symbols-outlined">cloud_download</span>
              <span>Exportar XLS (selecionados)</span>
            </button>
          </div>
          {estado?.erro && (
            <div className="tw-mt-3 tw-rounded-xl tw-border tw-border-rose-200 tw-bg-rose-50 tw-p-3 tw-text-sm tw-text-rose-700 dark:tw-border-rose-500/40 dark:tw-bg-rose-500/10 dark:tw-text-rose-100">
              {estado.erro}
            </div>
          )}
        </div>

        <div className="lote-progress-panel__list">
          {nenhumItem && (
            <div className="lote-progress-panel__empty">
              {statusAtual === "loading"
                ? "Consultando débitos em lote..."
                : "Nenhum veículo foi consultado recentemente."}
            </div>
          )}

          {!nenhumItem &&
            itensUnicos.map((item, index) => {
              const posicaoCard = index + 1;
              const veiculo = item.veiculo;
              const multasVeiculo = extrairMultas(veiculo || {});
              const statusVisual = mapearStatusItemVisual(item.status);
              const resumoItem = veiculo
                ? formatarResumoItem(veiculo)
                : { texto: item.mensagem || statusVisual.rotulo, variante: statusVisual.variante };
              const varianteFinal = resumoItem.variante || statusVisual.variante || "pending";
              const resumoTextoFinal = resumoItem.texto || statusVisual.rotulo;
              const erroAtual = item.erro || veiculo?.erro;
              const chaveItem = veiculo?.renavam || item.renavam || item.jobItemId || index;
              const renavamArquivo = normalizarRenavamChave(veiculo?.renavam || item.renavam || "");
              const responseBody = item.responseBody || {};
              const resumoMensagem = responseBody.resumoMensagem;
              const ipvaInfo = responseBody.ipva;
              const ipvaGuias = normalizarGuiaLista(ipvaInfo?.payload);
              const multasDetalhes = responseBody.multas || {};
              const multasGuias = normalizarGuiaLista(multasDetalhes.guias);
              const multasConsultaLista = normalizarMultasConsulta(multasDetalhes?.consulta);
              const multas = multasVeiculo.length ? multasVeiculo : multasConsultaLista;
              const licenciamentoInfo = responseBody.licenciamento || {};
              const licenciamentoGuiasValidos = normalizarGuiaLista(licenciamentoInfo.guiaAtual)
                .filter(guiaPossuiDadosPagamento)
                .slice(0, 1);
              const consultaMultasMensagem =
                multasDetalhes?.consulta?.message || multasDetalhes?.consulta?.status || null;
              const ipvaMensagem = ipvaInfo?.message || ipvaInfo?.status || null;
              const licenciamentoMensagens = extrairMensagensLicenciamento(licenciamentoInfo);
              return (
                <div
                  key={chaveItem}
                  className="lote-progress-item"
                  style={CARD_VARIANT_STYLES[varianteFinal] || CARD_VARIANT_STYLES.default}
                >
                  <div className="lote-progress-item__header">
                    <div className="tw-flex tw-w-full tw-flex-col">
                      <span className="tw-text-[11px] tw-font-semibold tw-text-slate-500 dark:tw-text-slate-300">
                        #{posicaoCard}
                      </span>
                      <div className="tw-flex tw-flex-col tw-gap-0.5">
                        <strong className="tw-text-base tw-text-slate-900 dark:tw-text-slate-100">
                          RENAVAM: {veiculo?.renavam || item.renavam || "-"}
                        </strong>
                        {veiculo?.chassi && (
                          <span className="lote-progress-item__arquivo">{`Chassi: ${veiculo.chassi}`}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="tw-flex tw-flex-wrap tw-gap-2 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
                    {veiculo?.tipoVeiculo && (
                      <span className="tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-1 dark:tw-bg-slate-800">
                        {veiculo.tipoVeiculo}
                      </span>
                    )}
                    {veiculo?.categoria && (
                      <span className="tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-1 dark:tw-bg-slate-800">
                        {veiculo.categoria}
                      </span>
                    )}
                    {veiculo?.situacao && (
                      <span className="tw-rounded-full tw-bg-slate-100 tw-px-2 tw-py-1 dark:tw-bg-slate-800">
                        {veiculo.situacao}
                      </span>
                    )}
                  </div>

                  {item.mensagem && item.mensagem !== resumoTextoFinal && (
                    <p className="tw-mt-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-300">
                      {item.mensagem}
                    </p>
                  )}

                  {erroAtual && (
                    <p className="tw-mt-1 tw-text-xs tw-font-semibold tw-text-rose-600 dark:tw-text-rose-300">
                      {erroAtual}
                    </p>
                  )}

                  {resumoMensagem && (
                    <p className="tw-mt-1 tw-text-xs tw-text-amber-600 dark:tw-text-amber-300">
                      {resumoMensagem}
                    </p>
                  )}

                  {consultaTiposNormalizados.multas && (
                    <div className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3 dark:tw-border-slate-700 dark:tw-bg-slate-900/50">
                      <div className="tw-flex tw-items-center tw-justify-between">
                        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                          Multas ({multas.length})
                        </p>
                      </div>
                      {multas.length === 0 && (
                        <p className="tw-m-0 tw-text-sm tw-text-slate-500 dark:tw-text-slate-300">
                          Nenhuma multa retornada.
                        </p>
                      )}
                      {multas.length > 0 && (
                        <div className="tw-mt-2 tw-overflow-x-auto">
                          <table className="table tw-text-sm">
                            <thead>
                              <tr>
                                <th>Auto</th>
                                <th>Infração</th>
                                <th>Data/Hora</th>
                                <th>Valor (orig/atual)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {multas.map((multa, idx) => (
                                <tr key={`${multa?.codAuto || multa?.codigo || idx}-${idx}`}>
                                  <td>
                                    {multa?.codAuto || multa?.codigo || "-"}
                                    <br />
                                    <span className="email">{multa?.nomeOrgaoAutuador || multa?.orgao || "-"}</span>
                                  </td>
                                  <td>
                                    {multa?.descrInfracao || multa?.descricao || "-"}
                                    <br />
                                    <span className="email">{multa?.enquadramento || multa?.tipoDebito || multa?.tipo || "-"}</span>
                                  </td>
                                  <td>
                                    {formatarDataCurta(multa?.dataInfracao || multa?.data) || "-"}
                                    <br />
                                    <span className="email">{multa?.horaInfracao || multa?.hora || "-"}</span>
                                  </td>
                                  <td>
                                    {multa?.valorOriginal || multa?.valor || "-"}
                                    <br />
                                    <span className="email">{multa?.valorACobrar || multa?.valorAtualizado || "-"}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {consultaTiposNormalizados.ipva && ipvaInfo && (
                    <div className="tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3 dark:tw-border-amber-400/40 dark:tw-bg-amber-400/10">
                      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-amber-900 dark:tw-text-amber-100">
                          IPVA
                        </p>
                        {ipvaGuias.length > 0 &&
                          (() => {
                            const primeiro = ipvaGuias[0];
                            const vencimentoGuias =
                              extrairVencimentoPagamento(primeiro) ||
                              extrairVencimentoPagamento(primeiro?.guia) ||
                              "";
                            return (
                              <span className="tw-text-xs tw-font-semibold tw-text-amber-800 dark:tw-text-amber-200">
                                {vencimentoGuias ? `Vence em ${formatarDataCurta(vencimentoGuias)}` : ""}
                              </span>
                            );
                          })()}
                      </div>
                      {ipvaMensagem && (
                        <p className="tw-mt-1 tw-text-xs tw-text-amber-800 dark:tw-text-amber-200">
                          {ipvaMensagem}
                        </p>
                      )}
                      {ipvaGuias.length === 0 && (
                        <p className="tw-mt-1 tw-text-sm tw-text-amber-800 dark:tw-text-amber-100">
                          Nenhuma guia retornada.
                        </p>
                      )}
                      {ipvaGuias.length > 0 && (
                        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-2">
                          {ipvaGuias.map((guia, idx) => {
                            const guiaPdfBase64 = extrairPdfBase64(guia);
                            const referenciaGuia = extrairTipoDescricao("IPVA", guia, "");
                            return (
                              <div key={`ipva-guia-${idx}`} className="tw-flex tw-flex-col tw-gap-2">
                                {renderGuiaPagamento(guia, `ipva-${idx}`)}
                                {guiaPdfBase64 && (
                                  <div>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-sm"
                                      onClick={() =>
                                        salvarPdfBase64(
                                          montarNomeArquivoGuia(renavamArquivo, {
                                            tipo: "ipva",
                                            referencia: referenciaGuia,
                                          }),
                                          guiaPdfBase64,
                                        )
                                      }
                                    >
                                      Baixar guia (PDF)
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {consultaTiposNormalizados.multas && (multasGuias.length > 0 || consultaMultasMensagem) && (
                    <div className="tw-rounded-xl tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-p-3 dark:tw-border-indigo-400/40 dark:tw-bg-indigo-400/10">
                      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-indigo-900 dark:tw-text-indigo-100">
                          Guias de Multa
                        </p>
                        <span className="tw-text-xs tw-uppercase tw-text-indigo-700 dark:tw-text-indigo-200">
                          {multasGuias.length} guia(s)
                        </span>
                      </div>
                      {consultaMultasMensagem && (
                        <p className="tw-mt-1 tw-text-xs tw-text-indigo-800 dark:tw-text-indigo-200">
                          {consultaMultasMensagem}
                        </p>
                      )}
                      {multasGuias.length === 0 && (
                        <p className="tw-mt-1 tw-text-sm tw-text-indigo-800 dark:tw-text-indigo-100">
                          Nenhuma guia retornada.
                        </p>
                      )}
                      {multasGuias.length > 0 && (
                        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-2">
                          {multasGuias.map((entrada, idx) => (
                            <div
                              key={`${entrada?.codAuto || entrada?.codigo || idx}-${idx}`}
                              className="tw-rounded-lg tw-border tw-border-indigo-200 tw-bg-white tw-p-2 dark:tw-border-indigo-500/30 dark:tw-bg-slate-900/40"
                            >
                              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                                <strong>{entrada?.codAuto || entrada?.codigo || "Auto"}</strong>
                                {(() => {
                                  const guiaVencimento =
                                    entrada?.guia?.dataVencimentoGRD ||
                                    entrada?.guia?.dataVencimento ||
                                    entrada?.guia?.vencimento ||
                                    entrada?.dataVencimentoAuto ||
                                    entrada?.dataLimitePagto ||
                                    "";
                                  const guiaVencimentoFormatado = guiaVencimento
                                    ? formatarDataCurta(guiaVencimento)
                                    : "";
                                  return guiaVencimentoFormatado ? (
                                    <span className="tw-text-xs tw-font-semibold tw-text-indigo-600 dark:tw-text-indigo-200">
                                      Vence em {guiaVencimentoFormatado}
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                              {entrada?.guia ? (
                                <>
                                  <div className="tw-mt-2">
                                    {renderGuiaPagamento(entrada.guia, idx)}
                                  </div>
                                  {entrada.guia.guiaBase64 && (
                                    <div className="tw-mt-2">
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() =>
                                          salvarPdfBase64(
                                            `guia-multa-${veiculo?.renavam || item.renavam || entrada?.codAuto || idx}.pdf`,
                                            entrada.guia.guiaBase64,
                                          )
                                        }
                                      >
                                        Baixar guia (PDF)
                                      </button>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <p className="tw-mt-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-300">
                                  Nenhuma guia vinculada.
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {consultaTiposNormalizados.licenciamento &&
                    (licenciamentoGuiasValidos.length > 0 || licenciamentoMensagens.length > 0) && (
                    <div className="tw-rounded-xl tw-border tw-border-emerald-200 tw-bg-emerald-50 tw-p-3 dark:tw-border-emerald-400/40 dark:tw-bg-emerald-400/10">
                      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-emerald-900 dark:tw-text-emerald-100">
                          Licenciamento
                        </p>
                        {licenciamentoGuiasValidos.length > 0 && (
                          <span className="tw-text-xs tw-uppercase tw-text-emerald-700 dark:tw-text-emerald-200">
                            {licenciamentoGuiasValidos.length} guia(s)
                          </span>
                        )}
                      </div>
                      {licenciamentoMensagens.map((texto, idx) => (
                        <p key={idx} className="tw-mt-1 tw-text-xs tw-text-emerald-800 dark:tw-text-emerald-200">
                          {texto}
                        </p>
                      ))}
                      {licenciamentoGuiasValidos.length > 0 ? (
                        <div className="tw-mt-2">
                          {licenciamentoGuiasValidos.map((guia, idx) => renderGuiaPagamento(guia, `ga-${idx}`))}
                        </div>
                      ) : (
                        licenciamentoMensagens.length === 0 && (
                          <p className="tw-mt-2 tw-text-xs tw-text-emerald-800 dark:tw-text-emerald-200">
                            Nenhuma guia de licenciamento retornada pelo DETRAN.
                          </p>
                        )
                      )}
                    </div>
                  )}

                  <details className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-p-3 tw-text-sm tw-text-slate-700 dark:tw-border-slate-700 dark:tw-bg-slate-900/60 dark:tw-text-slate-200">
                    <summary className="tw-cursor-pointer tw-font-semibold">Ver resposta completa</summary>
                    <pre className="tw-mt-2 tw-max-h-64 tw-overflow-auto tw-rounded-lg tw-bg-slate-900 tw-p-3 tw-text-xs tw-text-slate-100">
                      {JSON.stringify(item.responseBody || veiculo || item, null, 2)}
                    </pre>
                  </details>
                </div>
              );
            })}
        </div>
      </aside>
    </div>
  );
}

export default DebitosLotePanel;
